import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import authService from '../../services/authService'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff, Loader2, Check, User, Mail, Phone, Lock, ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react'

const steps = [
  { id: 1, titleKey: 'auth.stepPersonal', icon: User },
  { id: 2, titleKey: 'auth.stepContact', icon: Mail },
  { id: 3, titleKey: 'auth.stepSecurity', icon: Lock },
]

// Account-type options — these lead to profile completion (userType selection).
// titleKey/descKey resolve via i18n so the whole form switches language.
// Account-type options — these lead to profile completion (userType selection).
// titleKey/descKey resolve via i18n so the whole form switches language.
// Doctor / Health Worker personas were retired; existing users of those types
// keep working but can no longer self-register as one (see userTypeDestination).
const ACCOUNT_ROLES = [
  { id: 'CITIZEN', titleKey: 'auth.roleCitizen', descKey: 'auth.roleCitizenDesc', icon: '👤' },
]

// Supporter / Ni-kshay Mitra options — ids match the real
// SupporterRegistration.registrationType enum (POST /supporters/register).
// Selecting one of these hands off to the supporter registration form after
// the account is created, so the choice leads to a real submission, not dead UI.
// CSR Partnership was retired as a self-serve option.
const SUPPORTER_ROLES = [
  { id: 'NIKSHAY_MITRA_INDIVIDUAL', titleKey: 'auth.supporterIndividual', descKey: 'auth.supporterIndividualDesc', icon: '🤝' },
  { id: 'NIKSHAY_MITRA_NGO', titleKey: 'auth.supporterNgo', descKey: 'auth.supporterNgoDesc', icon: '🏢' },
  { id: 'NIKSHAY_MITRA_CORPORATE', titleKey: 'auth.supporterCorporate', descKey: 'auth.supporterCorporateDesc', icon: '🏛️' },
  { id: 'NUTRITION_SPONSOR', titleKey: 'auth.nutritionSponsor', descKey: 'auth.nutritionSponsorDesc', icon: '🍎' },
  { id: 'VOLUNTEER', titleKey: 'auth.volunteerRole', descKey: 'auth.volunteerRoleDesc', icon: '🙋' },
]

const RegisterPage = () => {
  const { t } = useTranslation()
  const { login, setPendingRole } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [agreed, setAgreed] = useState(false)

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    gender: '',
    role: 'CITIZEN',
  })

  const passwordStrength = () => {
    const { password } = formData
    let strength = 0
    if (password.length >= 8) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++
    return strength
  }

  const getPasswordStrengthText = () => {
    const strength = passwordStrength()
    if (strength <= 2) return { text: t('auth.weak'), color: 'text-red-500', bg: 'bg-red-500' }
    if (strength <= 3) return { text: t('auth.medium'), color: 'text-yellow-500', bg: 'bg-yellow-500' }
    if (strength <= 4) return { text: t('auth.strong'), color: 'text-green-500', bg: 'bg-green-500' }
    return { text: t('auth.veryStrong'), color: 'text-emerald-500', bg: 'bg-emerald-500' }
  }

  const isStepValid = () => {
    if (currentStep === 1) return formData.firstName && formData.lastName && formData.gender && formData.role
    if (currentStep === 2) return formData.email && formData.phone
    if (currentStep === 3) return formData.password && formData.confirmPassword && formData.password === formData.confirmPassword && agreed
    return false
  }

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1)
  }

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isStepValid()) return

    setError(null)
    setIsLoading(true)

    try {
      const response = await authService.register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
        gender: formData.gender || undefined,
      })

      // Record the chosen role so the /register route guard can route to the
      // correct post-register destination (supporter form vs role onboarding)
      // from a single source. We do NOT navigate() from here: doing so races
      // with the guard's <Navigate> that fires when `login()` flips
      // isAuthenticated to true, and the guard would win — dropping the role
      // and stranding the user on the "select your role" screen.
      setPendingRole(formData.role)
      login(response.user, response.accessToken, response.refreshToken)
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.registrationFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <Link to="/" className="inline-flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
            <span className="text-2xl">🇮🇳</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('common.appName')}</h1>
            <p className="text-xs text-gray-500">{t('auth.nationalHealthMission')}</p>
          </div>
        </Link>
      </motion.div>

      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 p-8"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{t('auth.createAccount')}</h2>
          <p className="text-gray-500 mt-1">{t('auth.registerSubtitle')}</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((step, index) => {
            const Icon = step.icon
            return (
              <div key={step.id} className="flex items-center">
                <motion.div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    currentStep >= step.id
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                  animate={{ scale: currentStep === step.id ? 1.1 : 1 }}
                  aria-label={t(step.titleKey)}
                >
                  {currentStep > step.id ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </motion.div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-1 rounded-full mx-2 transition-all ${
                    currentStep > step.id ? 'bg-gradient-to-r from-blue-600 to-cyan-500' : 'bg-gray-100'
                  }`} />
                )}
              </div>
            )
          })}
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200"
          >
            <p className="text-red-600 text-sm text-center">{error}</p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {/* Step 1: Personal Info */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">{t('auth.iWantToJoinAs')}</label>
                  <div className="relative">
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all bg-white appearance-none cursor-pointer"
                    >
                      <option value="" disabled>{t('auth.selectRole')}</option>
                      <optgroup label={t('auth.accountType')}>
                        {ACCOUNT_ROLES.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.icon} {t(role.titleKey)}{t(role.descKey) ? ` — ${t(role.descKey)}` : ''}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('auth.tbSupporters')}>
                        {SUPPORTER_ROLES.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.icon} {t(role.titleKey)}{t(role.descKey) ? ` — ${t(role.descKey)}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.firstName')} *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                        placeholder={t('auth.firstNamePlaceholder')}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.lastName')} *</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      placeholder={t('auth.lastNamePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.gender')} *</label>
                  <select
                    required
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all bg-white appearance-none cursor-pointer"
                  >
                    <option value="">{t('auth.selectGender')}</option>
                    <option value="MALE">{t('auth.male')}</option>
                    <option value="FEMALE">{t('auth.female')}</option>
                    <option value="OTHER">{t('auth.other')}</option>
                    <option value="PREFER_NOT_TO_SAY">{t('auth.preferNotToSay')}</option>
                  </select>
                </div>
              </motion.div>
            )}

            {/* Step 2: Contact Info */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.emailAddress')} *</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      placeholder={t('auth.emailPlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.phone')} *</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      placeholder="+91 9876543210"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 ml-1">{t('auth.sendOtpNote')}</p>
                </div>

                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <p className="text-sm text-blue-700">
                    📱 {t('auth.phoneNotice')}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 3: Security */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')} *</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full pl-11 pr-12 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      placeholder={t('auth.createPasswordPlaceholder')}
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Password Strength */}
                  {formData.password && (
                    <div className="mt-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-500">{t('auth.passwordStrength')}</span>
                        <span className={`text-xs font-medium ${getPasswordStrengthText().color}`}>
                          {getPasswordStrengthText().text}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`h-1.5 flex-1 rounded-full transition-all ${
                              passwordStrength() >= i ? getPasswordStrengthText().bg : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.confirmPassword')} *</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="w-full pl-11 pr-12 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      placeholder={t('auth.confirmPasswordPlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-xs text-red-500 mt-2 ml-1">{t('auth.passwordsDoNotMatch')}</p>
                  )}
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">
                    {t('auth.iAgreeToPrefix')}{' '}
                    <Link to="/terms" className="text-blue-600 hover:underline font-medium">{t('footer.termsOfService')}</Link>
                    {' '}{t('auth.andWord')}{' '}
                    <Link to="/privacy" className="text-blue-600 hover:underline font-medium">{t('footer.privacyPolicy')}</Link>
                  </span>
                </label>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-8">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </button>
            )}

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={!isStepValid()}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
              >
                {t('auth.continueBtn')}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading || !isStepValid()}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('auth.creating')}
                  </>
                ) : (
                  <>
                    {t('auth.createAccount')}
                    <Check className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Login Link */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="text-blue-600 font-medium hover:underline">
            {t('auth.signIn')}
          </Link>
        </p>
      </motion.div>

      {/* Footer */}
      <p className="text-center text-xs text-gray-400 mt-6">
        © 2026 {t('common.appName')}. {t('auth.govtInitiative')}
      </p>
    </div>
  )
}

export default RegisterPage